import { useState, useCallback } from 'react';

export type ConsentStatus = 'unknown' | 'accepted' | 'refused' | 'custom';

export interface CookieConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
}

export interface CookieConsentState {
  status: ConsentStatus;
  preferences: CookieConsentPreferences | null;
  showBanner: boolean;
  showPreferences: boolean;
}

const STORAGE_KEY = 'trezoone_cookie_consent';

function loadFromStorage(): { status: ConsentStatus; preferences: CookieConsentPreferences | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { status: 'unknown', preferences: null };
    const parsed = JSON.parse(raw);
    return {
      status: parsed.status ?? 'unknown',
      preferences: parsed.preferences ?? null,
    };
  } catch {
    return { status: 'unknown', preferences: null };
  }
}

function saveToStorage(status: ConsentStatus, preferences: CookieConsentPreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ status, preferences }));
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

function updateGtagConsent(analytics: boolean, marketing: boolean) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    const analyticsGranted = analytics ? 'granted' : 'denied';
    const marketingGranted = marketing ? 'granted' : 'denied';
    (window as any).gtag('consent', 'update', {
      analytics_storage: analyticsGranted,
      ad_storage: marketingGranted,
      ad_user_data: marketingGranted,
      ad_personalization: marketingGranted,
    });
    if (analytics) {
      (window as any).gtag('event', 'page_view');
    }
  }
  if (typeof window !== 'undefined' && (window as any).fbq) {
    if (marketing) {
      (window as any).fbq('consent', 'grant');
    } else {
      (window as any).fbq('consent', 'revoke');
    }
  }
}

export function useCookieConsent() {
  const [state, setState] = useState<CookieConsentState>(() => {
    const stored = loadFromStorage();
    return {
      status: stored.status,
      preferences: stored.preferences,
      showBanner: stored.status === 'unknown',
      showPreferences: false,
    };
  });

  const isAnalyticsAllowed = useCallback(() => {
    if (!state.preferences) return false;
    return state.preferences.analytics === true;
  }, [state.preferences]);

  const isMarketingAllowed = useCallback(() => {
    if (!state.preferences) return false;
    return state.preferences.marketing === true;
  }, [state.preferences]);

  const acceptAll = useCallback(() => {
    const prefs: CookieConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      updatedAt: new Date().toISOString(),
    };
    saveToStorage('accepted', prefs);
    updateGtagConsent(true, true);
    setState(prev => ({
      ...prev,
      status: 'accepted',
      preferences: prefs,
      showBanner: false,
      showPreferences: false,
    }));
  }, []);

  const refuseAll = useCallback(() => {
    const prefs: CookieConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      updatedAt: new Date().toISOString(),
    };
    saveToStorage('refused', prefs);
    updateGtagConsent(false, false);
    setState(prev => ({
      ...prev,
      status: 'refused',
      preferences: prefs,
      showBanner: false,
      showPreferences: false,
    }));
  }, []);

  const saveCustomPreferences = useCallback((analytics: boolean, marketing: boolean) => {
    const prefs: CookieConsentPreferences = {
      necessary: true,
      analytics,
      marketing,
      updatedAt: new Date().toISOString(),
    };
    const status: ConsentStatus = analytics || marketing ? 'custom' : 'refused';
    saveToStorage(status, prefs);
    updateGtagConsent(analytics, marketing);
    setState(prev => ({
      ...prev,
      status,
      preferences: prefs,
      showBanner: false,
      showPreferences: false,
    }));
  }, []);

  const openPreferences = useCallback(() => {
    setState(prev => ({ ...prev, showPreferences: true, showBanner: false }));
  }, []);

  const closePreferences = useCallback(() => {
    setState(prev => ({
      ...prev,
      showPreferences: false,
      showBanner: prev.status === 'unknown',
    }));
  }, [state.status]);

  return {
    status: state.status,
    preferences: state.preferences,
    showBanner: state.showBanner,
    showPreferences: state.showPreferences,
    isAnalyticsAllowed,
    isMarketingAllowed,
    acceptAll,
    refuseAll,
    saveCustomPreferences,
    openPreferences,
    closePreferences,
  };
}
